import { A, D, G, pipe } from '@mobily/ts-belt';
import { Box, SxProps } from '@mui/material';
import { get } from 'lodash';
import { cloneElement, ReactElement } from 'react';
import { RaRecord, useRecordContext, useResourceContext, useTranslate } from 'react-admin';

import { subtitleStyle } from '@sh4res/bo-common/components/ShowSection';
import { PathImpl } from '@sh4res/bo-common/utils/types';

type KeysWithDotNotation<T> = T extends Record<string, unknown> ? PathImpl<T, keyof T> : never;

type FieldKey<T> = KeysWithDotNotation<T> | keyof T;

interface ShowFieldsProps<T extends object> {
  fields: Partial<Record<FieldKey<T>, ReactElement>>;
  labels?: Partial<Record<FieldKey<T>, string>>;
  labelTranslationParams?: Partial<Record<FieldKey<T>, Record<string, unknown>>>;
  borderBottom?: boolean;
  resource?: string;
  labelStyle?: Partial<Record<FieldKey<T>, SxProps>>;
  labelPrefix?: string;
  subtitles?: Partial<Record<FieldKey<T>, string>>;
  hideWhenEmpty?: boolean;
}

const isFieldValueEmpty = (record: RaRecord, fieldKey: string, field: ReactElement): boolean => {
  const effectiveRecord = (field.props?.record as RaRecord | undefined) ?? record;
  const source = (field.props?.source as string | undefined) ?? fieldKey;
  const value = get(effectiveRecord, source);

  return G.isNullable(value) || (Array.isArray(value) && A.isEmpty(value));
};

interface ShowFieldRowProps {
  fieldKey: string;
  field: ReactElement;
  label: string;
  subtitle: string | null;
  labelStyle?: SxProps;
  emptyText: string;
}

const ShowFieldRow = ({
  fieldKey,
  field,
  label,
  subtitle,
  labelStyle,
  emptyText,
}: ShowFieldRowProps) => (
  <>
    <Box
      sx={{
        padding: '13px 30px 13px 15px',
        borderTop: '1px solid #E8E8E8',
        alignContent: 'center',
        ...labelStyle,
      }}>
      {label}
      {subtitle && <Box sx={{ ...subtitleStyle, marginTop: '-4px' }}>{subtitle}</Box>}
    </Box>
    <Box
      sx={{
        padding: '13px 15px 13px 15px',
        borderTop: '1px solid #E8E8E8',
        alignContent: 'center',
        overflow: 'auto',
      }}>
      {cloneElement(field, {
        source: fieldKey,
        emptyText,
      })}
    </Box>
  </>
);

export const ShowFields = <T extends object>({
  fields,
  borderBottom = true,
  resource,
  labels = {},
  labelTranslationParams = {},
  labelStyle = {},
  labelPrefix,
  subtitles,
  hideWhenEmpty = false,
}: ShowFieldsProps<T>) => {
  const finalResource = useResourceContext({ resource });
  const translate = useTranslate();
  const record = useRecordContext();

  const rows = pipe(
    fields,
    D.toPairs,
    A.filterMap(([fieldKey, field]) => {
      if (field === undefined) {
        return undefined;
      }

      const ownEmptyText = field.props?.emptyText as string | undefined;

      if (
        hideWhenEmpty &&
        ownEmptyText === undefined &&
        record &&
        isFieldValueEmpty(record, fieldKey, field)
      ) {
        return undefined;
      }

      const typedFieldKey = fieldKey as FieldKey<T>;
      const labelKey =
        labels[typedFieldKey] ||
        `${labelPrefix || `resources.${finalResource}.fields`}.${fieldKey}`;

      return (
        <ShowFieldRow
          key={fieldKey}
          fieldKey={fieldKey}
          field={field}
          label={translate(
            labelKey as Parameters<typeof translate>[0],
            labelTranslationParams[typedFieldKey] ?? {},
          )}
          subtitle={
            subtitles
              ? translate(subtitles[typedFieldKey] as Parameters<typeof translate>[0])
              : null
          }
          labelStyle={labelStyle[typedFieldKey]}
          emptyText={ownEmptyText ?? '-'}
        />
      );
    }),
  );

  if (hideWhenEmpty && A.isEmpty(rows)) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: '34% 66%',
        borderBottom: borderBottom ? '1px solid #E8E8E8' : 'none',
        '& .RaImageField-image': { verticalAlign: 'middle' },
        '& .MuiTypography-root': { fontSize: theme.typography.body1, margin: '0px' },
      })}>
      {rows}
    </Box>
  );
};
